import { useEffect } from 'react';

// What a sheet leaves of the page above itself, so it reads as a sheet and not as the screen.
const TOP_GAP = 16;

/** The tallest a sheet may be: what the user can actually see, less the gap above it. */
export const SHEET_MAX_HEIGHT_VAR = '--swipe-sheet-max-height';

/** How far the sheet has to rise off the layout viewport's bottom to sit on the visible one. */
export const SHEET_LIFT_VAR = '--swipe-sheet-lift';

/**
 * Sizes a bottom sheet against the software keyboard.
 *
 * A sheet cannot ask CSS how big the screen is. `dvh`, `svh` and a percentage of the fixed
 * containing block all measure the layout viewport, and a keyboard is not obliged to change it —
 * iOS has historically left it alone and shrunk only the *visual* viewport. So a sheet asking for
 * most of the screen keeps asking for most of a screen that is no longer there, and wears its own
 * header off the top.
 *
 * `visualViewport.height` is the one number that is true either way, and it is all the height here
 * is built from. Deriving the keyboard from the gap between the two viewports instead —
 * `clientHeight - offsetTop - height` — reads as exactly zero on the versions that shrink both, and
 * fails silently: the sheet keeps its full height and rides up off the top of the screen.
 *
 * The lift is the gap between the two viewports' bottoms, and is allowed to be zero. A sheet is
 * fixed to the layout viewport's bottom edge, and whether that edge is the one the user can see is
 * not iOS's habit but its mood: opening a keyboard on a fresh sheet scrolls the page so the two
 * line up, and opening it again by tapping the field does not. The lift closes that gap, and is
 * zero in the cases where iOS already closed it.
 *
 * Both numbers go to the document root as custom properties rather than through a ref: there is one
 * keyboard, the sheet is not always mounted, and this way nothing depends on a component library
 * passing a ref through to the node. They are measured per frame, because iOS moves the visual
 * viewport for reasons it does not always announce, and written only when they change, so a settled
 * sheet costs a comparison a frame.
 */
export function useKeyboardFrame(open: boolean): void {
  useEffect(() => {
    if (!open) {
      return;
    }

    const viewport = window.visualViewport;
    if (!viewport) {
      return;
    }

    const root = document.documentElement;

    let frame = 0;
    let lastMaxHeight = '';
    let lastLift = '';

    const fitToKeyboard = () => {
      const maxHeight = `${Math.max(0, viewport.height - TOP_GAP)}px`;
      const lift = `${Math.max(0, root.clientHeight - viewport.offsetTop - viewport.height)}px`;

      if (maxHeight !== lastMaxHeight) {
        root.style.setProperty(SHEET_MAX_HEIGHT_VAR, maxHeight);
        lastMaxHeight = maxHeight;
      }

      if (lift !== lastLift) {
        root.style.setProperty(SHEET_LIFT_VAR, lift);
        lastLift = lift;
      }

      frame = requestAnimationFrame(fitToKeyboard);
    };

    fitToKeyboard();
    return () => {
      cancelAnimationFrame(frame);
      root.style.removeProperty(SHEET_MAX_HEIGHT_VAR);
      root.style.removeProperty(SHEET_LIFT_VAR);
    };
  }, [open]);
}
